import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  Cell,
  PolarAngleAxis,
  PolarGrid,
  RadialBar,
  type RadialBarDataItem,
  RadialBarChart as RechartsRadialBarChart,
} from 'recharts';
import {
  ChartLegend,
  type ChartSeries,
} from '@/components/molecules/charts/LineChart';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  useChartKeyColors,
} from '@/components/ui/chart';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { useSeriesHighlight } from '@/hooks/use-series-highlight';
import { cn } from '@/lib/utils';
import type { PieChartItem } from '@/types/data';
import {
  getFiniteChartValue,
  getFinitePositiveChartValue,
  getNonNegativeChartValue,
} from '../chart-data';

const radialChartVariants = cva('relative max-w-full', {
  variants: {
    size: {
      xs: 'size-[160px]',
      sm: 'size-[200px]',
      md: 'size-60',
      lg: 'size-[280px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type RadialLegendPosition = 'none' | 'bottom' | 'right';
export type RadialChartShape = 'circle' | 'gauge';

export interface RadialChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof radialChartVariants> {
  /** One item per radial band. */
  data: PieChartItem[];
  /** Optional legend labels. Defaults to data labels. */
  series?: ChartSeries[];
  /** Full-circle rings or a half-circle gauge. */
  shape?: RadialChartShape;
  /** Maximum value represented by a complete arc. */
  maxValue?: number;
  /** Center metric. */
  centerValue?: string | number;
  /** Center metric label. */
  centerLabel?: string;
  /** Legend placement. */
  legendPosition?: RadialLegendPosition;
  /** Show the muted remainder for each arc. */
  showBackground?: boolean;
  /** Show polar guide lines. */
  showGrid?: boolean;
  /** Arc thickness in pixels. */
  barSize?: number;
  /** Gap between radial bands in pixels. */
  barGap?: number;
  /** Arc corner radius in pixels. */
  cornerRadius?: number;
  /** Start angle in degrees. */
  startAngle?: number;
  /** End angle in degrees. */
  endAngle?: number;
  /** Show tooltip on hover. */
  showTooltip?: boolean;
  /** Suffix appended to the numeric tooltip value. */
  valueSuffix?: string;
  /** Accessible label for screen readers. */
  'aria-label'?: string;
}

// Recharts snapshots the visible sectors when retargeting. Match business IDs,
// not positions. New rings and changed scales commit without a false sweep.
const matchRing = (item: RadialBarDataItem) => item.payload.id;
const interpolateRings: NonNullable<
  React.ComponentProps<typeof RadialBar>['animationInterpolateFn']
> = (items, progress) =>
  (items ?? []).flatMap((item) => {
    if (item.status === 'removed') return [];
    if (item.status === 'added') return [item.next];
    if (item.prev.payload.radialScale !== item.next.payload.radialScale)
      return [item.next];
    return [
      {
        ...item.next,
        endAngle:
          item.prev.endAngle +
          (item.next.endAngle - item.prev.endAngle) * progress,
      },
    ];
  });

function formatCenterValue(value: string | number): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString()
    : String(value);
}

/**
 * @llms Concentric progress rings and gauges with center metrics, guides, and legends
 */
function RadialChart({
  data,
  series,
  shape = 'circle',
  maxValue,
  centerValue,
  centerLabel,
  legendPosition = 'none',
  showBackground = true,
  showGrid = false,
  barSize = 12,
  barGap = 4,
  cornerRadius = 8,
  startAngle,
  endAngle,
  showTooltip = true,
  valueSuffix = '',
  size,
  className,
  'aria-label': ariaLabel,
  ...props
}: RadialChartProps) {
  const prefersReducedMotion = useReducedMotion();
  const { highlightedSeries, setHighlightedSeries, getOpacity, dimTransition } =
    useSeriesHighlight(prefersReducedMotion);
  const derivedMaxValue = Math.max(
    100,
    ...data.map((item) => getNonNegativeChartValue(item.value)),
  );
  const finiteMaxValue = getFinitePositiveChartValue(maxValue, derivedMaxValue);
  const finiteBarSize = getFinitePositiveChartValue(barSize, 12);
  const finiteBarGap = getNonNegativeChartValue(barGap, 4);
  const finiteCornerRadius = getNonNegativeChartValue(cornerRadius, 8);
  const resolvedStartAngle = getFiniteChartValue(
    startAngle,
    shape === 'gauge' ? 180 : 90,
  );
  const resolvedEndAngle = getFiniteChartValue(
    endAngle,
    shape === 'gauge' ? 0 : -270,
  );

  const radialScale = `${shape}:${finiteMaxValue}:${resolvedStartAngle}:${resolvedEndAngle}`;
  const keyColors = useChartKeyColors(
    data.map((item, index) => item.id ?? index),
  );
  const safeData = React.useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        value: Math.min(getNonNegativeChartValue(item.value), finiteMaxValue),
        fill: item.color || keyColors.get(item.id ?? index),
        configKey: `ring-${index}`,
        radialScale,
      })),
    [data, finiteMaxValue, radialScale, keyColors],
  );
  const legendSeries = React.useMemo(
    () =>
      series?.map((item, index) => ({
        ...item,
        color:
          item.color ||
          safeData.find((datum) => datum.id === item.id)?.fill ||
          safeData[index]?.fill,
      })) ??
      safeData.map((item) => ({
        id: item.id,
        label: item.label,
        color: item.fill,
      })),
    [safeData, series],
  );
  const chartConfig = React.useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};
    safeData.forEach((item) => {
      config[item.configKey] = { label: item.label, color: item.fill };
    });
    return config;
  }, [safeData]);

  if (safeData.length === 0) {
    return (
      <div
        data-slot="radial-chart"
        className={cn(
          radialChartVariants({ size }),
          'flex items-center justify-center text-sm text-muted-foreground',
          className,
        )}
        role="img"
        aria-label={ariaLabel || 'Radial chart'}
        {...props}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      data-slot="radial-chart"
      className={cn(
        'flex',
        legendPosition === 'right' ? 'flex-row gap-4' : 'flex-col gap-3',
        legendPosition === 'bottom' && 'items-center',
      )}
      {...props}
    >
      <div
        data-slot="radial-chart-plot"
        role="img"
        aria-label={ariaLabel || 'Radial chart'}
        aria-labelledby={props['aria-labelledby']}
        className={cn(radialChartVariants({ size }), className)}
      >
        <ChartContainer config={chartConfig} className="size-full">
          <RechartsRadialBarChart
            data={safeData}
            innerRadius={shape === 'gauge' ? '58%' : '18%'}
            outerRadius={shape === 'gauge' ? '100%' : '96%'}
            barSize={finiteBarSize}
            barGap={finiteBarGap}
            startAngle={resolvedStartAngle}
            endAngle={resolvedEndAngle}
            cx="50%"
            cy={shape === 'gauge' ? '72%' : '50%'}
          >
            <PolarAngleAxis
              type="number"
              domain={[0, finiteMaxValue]}
              tick={false}
            />
            {showGrid && (
              <PolarGrid
                gridType="circle"
                radialLines
                className="stroke-border"
              />
            )}
            {showTooltip && (
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideLabel
                    formatter={(value, _name, item) => {
                      const payload = (item as { payload?: { label?: string } })
                        ?.payload;
                      return (
                        <div className="flex items-center gap-2">
                          <span>{payload?.label || 'Value'}</span>
                          <span className="font-medium">
                            {Number(value).toLocaleString()}
                            {valueSuffix}
                          </span>
                        </div>
                      );
                    }}
                  />
                }
              />
            )}
            <RadialBar
              // Reset the renderer snapshot on preference changes: Recharts
              // does not commit interrupted inactive snapshots (3.10.1).
              key={String(prefersReducedMotion)}
              dataKey="value"
              animationDuration={200}
              animationEasing="ease-out"
              animationMatchBy={matchRing}
              animationInterpolateFn={interpolateRings}
              background={showBackground ? { fill: 'var(--muted)' } : false}
              cornerRadius={finiteCornerRadius}
              isAnimationActive={!prefersReducedMotion}
            >
              {safeData.map((item) => (
                <Cell
                  key={item.id}
                  fill={item.fill}
                  opacity={getOpacity(item.id)}
                  style={dimTransition}
                />
              ))}
            </RadialBar>
          </RechartsRadialBarChart>
        </ChartContainer>

        {(centerValue !== undefined || centerLabel) && (
          <div
            data-slot="radial-chart-center"
            className={cn(
              'pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center',
              shape === 'gauge' && 'translate-y-[18%]',
            )}
          >
            {centerValue !== undefined && (
              <span className="text-2xl font-semibold text-foreground">
                {formatCenterValue(centerValue)}
              </span>
            )}
            {centerLabel && (
              <span className="text-xs text-muted-foreground">
                {centerLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {legendPosition !== 'none' && (
        <ChartLegend
          series={legendSeries}
          position={legendPosition === 'bottom' ? 'top' : 'right'}
          onSeriesHover={setHighlightedSeries}
          highlightedSeries={highlightedSeries}
        />
      )}
    </div>
  );
}

RadialChart.displayName = 'RadialChart';

export { RadialChart, radialChartVariants };
